import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { PageHeader, FilterBar } from "@/components/shared/PageChrome";
import { ViewTabs, SearchInput, InfoBanner, useListParams, ViewLink } from "@/components/shared/ListChrome";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const VIEWS = [{ value: "UPCOMING", label: "Upcoming" }, { value: "CURRENT", label: "Current" }, { value: "PAST", label: "Past" }, { value: "CANCELLED", label: "Cancellations" }, { value: "ALL", label: "All" }];
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function ClientBookings() {
  const { view, q, page, setView, setQ, setPage } = useListParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/client/bookings", { params: { view, q: q || undefined, page, page_size: 25 } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [view, q, page]);

  if (error) return <ErrorState testId="client-bookings-error" />;

  return (
    <div data-testid="client-bookings-page" className="space-y-5">
      <PageHeader title="Your Bookings" subtitle="View and track all confirmed bookings for your organisation." />

      <ViewTabs testId="client-bookings-views" tabs={VIEWS} value={view} onChange={setView} counts={data?.counts} />
      <FilterBar testId="client-bookings-filter-bar" summary={data ? `${data.total} booking${data.total === 1 ? "" : "s"} match` : null}>
        <SearchInput testId="client-bookings-search" value={q} onChange={setQ} placeholder="Search reference, property, location or guest…" />
      </FilterBar>

      {!data && <LoadingState testId="client-bookings-loading" />}
      {data && data.items.length === 0 && <EmptyState testId="client-bookings-empty" title={view === "ALL" && !q ? "No bookings yet" : "No bookings in this view"} description={view === "ALL" && !q ? "Confirmed bookings appear here once Jigsaw completes a request." : "Try another view or clear the search."} />}
      {data && data.items.length > 0 && (
        <div className="space-y-3">
          <StackedList testId="client-bookings-stacked" items={data.items} title={(b) => `${b.booking_reference}${b.property_name ? ` · ${b.property_name}` : ""}`} status={(b) => <StatusBadge status={b.status} />} onOpen={(b) => navigate(`/client/bookings/${b.id}`)} openLabel="Open booking"
            fields={[{ label: "Location", value: (b) => b.location }, { label: "Guest", value: (b) => b.lead_guest_name }, { label: "Dates", value: (b) => `${b.check_in_date || "—"} → ${b.check_out_date || "—"}`, mono: true, full: true }, { label: "Guests / rooms", value: (b) => `${b.total_guests} / ${b.total_rooms}` }]} />
          <div className="hidden sm:block">
          <Table data-testid="client-bookings-table">
            <TableHeader>
              <TableRow><TableHead>Property</TableHead><TableHead>Location</TableHead><TableHead>Guest(s)</TableHead><TableHead>Dates</TableHead><TableHead>Reference</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((b) => (
                <TableRow key={b.id} data-testid={`client-booking-row-${b.booking_reference}`} className="cursor-pointer hover:bg-jigsaw-bg" onClick={() => navigate(`/client/bookings/${b.id}`)}>
                  <TableCell className="font-medium text-jigsaw-navy">{b.property_name || <span className="text-jigsaw-muted">Not yet allocated</span>}</TableCell>
                  <TableCell>{b.location || "—"}</TableCell>
                  <TableCell>{b.lead_guest_name || "—"}{b.total_guests > 1 && <span className="text-jigsaw-muted"> +{b.total_guests - 1}</span>}</TableCell>
                  <TableCell className="whitespace-nowrap text-[13px]">{fmt(b.check_in_date)} &ndash; {fmt(b.check_out_date)}</TableCell>
                  <TableCell className="font-mono-ref text-[13px] text-jigsaw-navy">{b.booking_reference}</TableCell>
                  <TableCell><StatusBadge status={b.status} /></TableCell>
                  <TableCell className="text-right"><ViewLink testId={`client-booking-view-${b.booking_reference}`} onClick={() => navigate(`/client/bookings/${b.id}`)} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination testId="client-bookings-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}

      <InfoBanner testId="client-bookings-banner" icon={CalendarClock} title="Need to make a change?" text="Request an extension, cancellation or amendment from the booking itself, or message our support team." actionLabel="Contact Jigsaw" to="/client/support?intent=booking-change" />
    </div>
  );
}
